import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useAvailableAnimals, getImageUrl, AnimalGridSkeleton, searchAnimals, useTraitCanonicalizer } from '@rehome/shared';
import type { Species, Gender, Animal } from '@rehome/shared';

type AgeBucket = 'all' | 'baby' | 'young' | 'adult' | 'senior';
type SizeBucket = 'all' | 'small' | 'medium' | 'large';

/**
 * Animal List Page — SQL-powered filtering & search.
 * Uses server-side full-text search (Hungarian dictionary) for text queries.
 * Falls back to client-side filtering when Supabase is unavailable.
 * Honors the ?search= query param (deep link from the landing page hero).
 */
const AnimalListPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { data: supabaseAnimals, loading, error } = useAvailableAnimals();
  const { toCanonical } = useTraitCanonicalizer();
  const animals = supabaseAnimals || [];
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') ?? '');
  const [showFilters, setShowFilters] = useState(false);
  const [searchResults, setSearchResults] = useState<Animal[] | null>(null);
  const [searching, setSearching] = useState(false);

  // ─── Filters ───────────────────────────────────────────────────────────────
  const [filterSpecies, setFilterSpecies] = useState<Species | 'all'>('all');
  const [filterGender, setFilterGender] = useState<Gender | 'all'>('all');
  const [filterAge, setFilterAge] = useState<AgeBucket>('all');
  const [filterSize, setFilterSize] = useState<SizeBucket>('all');
  const [filterBreed, setFilterBreed] = useState<string>('all');
  const [filterColor, setFilterColor] = useState<string>('all');
  const [selectedTraits, setSelectedTraits] = useState<string[]>([]);
  const [onlyVaccinated, setOnlyVaccinated] = useState(false);
  const [onlyNeutered, setOnlyNeutered] = useState(false);
  const [onlyDewormed, setOnlyDewormed] = useState(false);

  // Server-side search with debounce (runs when searchTerm has 2+ chars)
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSearchResults(null);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      const results = await searchAnimals(searchTerm.trim(), 30);
      setSearchResults(results);
      setSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Determine which dataset to use
  const useServerSearch = searchResults !== null;
  const sourceData = useServerSearch ? searchResults : animals;

  // Filter option lists derived from the available animals (stable across the
  // current filter selection so options don't disappear as you narrow down).
  const breedOptions = useMemo(() => {
    const pool = filterSpecies === 'all' ? animals : animals.filter(a => a.species === filterSpecies);
    return Array.from(new Set(pool.map(a => a.breed).filter((b): b is string => !!b)))
      .sort((a, b) => a.localeCompare(b, 'hu'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseAnimals, filterSpecies]);

  // A chipek a közös tulajdonság-szótár kanonikus kulcsszavai. A menhelyek
  // szinonimáit (pl. "kedves" -> "Barátságos") a szótár fogja össze, így egy
  // jelentésből egy chip lesz — a még egységesítés előtt rögzített, régebbi
  // állatoknál is (033_trait_vocabulary.sql).
  const traitsOf = useMemo(
    () => (a: Animal) => Array.from(new Set((a.traits || []).map(toCanonical).filter(Boolean))),
    [toCanonical]
  );

  const traitOptions = useMemo(
    () => Array.from(new Set(animals.flatMap(traitsOf))).sort((a, b) => a.localeCompare(b, 'hu')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supabaseAnimals, traitsOf]
  );

  const colorOptions = useMemo(
    () => Array.from(new Set(animals.map(a => a.color).filter((c): c is string => !!c))).sort((a, b) => a.localeCompare(b, 'hu')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supabaseAnimals]
  );

  // A kiválasztott chipeket is a kanonizálón keresztül hasonlítjuk össze: a
  // szótár aszinkron töltődik, így előfordulhat, hogy a felhasználó még a
  // nyers címkét választotta ki, mire a leképezés megérkezik. Enélkül a
  // szűrő némán 0 találatot adna egy olyan chipre, ami épp átnevezésre került.
  const isTraitSelected = (t: string) => selectedTraits.some(sel => toCanonical(sel) === t);

  const toggleTrait = (t: string) =>
    setSelectedTraits(prev => (isTraitSelected(t) ? prev.filter(x => toCanonical(x) !== t) : [...prev, t]));

  const clearFilters = () => {
    setFilterSpecies('all');
    setFilterGender('all');
    setFilterAge('all');
    setFilterSize('all');
    setFilterBreed('all');
    setFilterColor('all');
    setSelectedTraits([]);
    setOnlyVaccinated(false);
    setOnlyNeutered(false);
    setOnlyDewormed(false);
  };

  const activeFilterCount =
    (filterSpecies !== 'all' ? 1 : 0) +
    (filterGender !== 'all' ? 1 : 0) +
    (filterAge !== 'all' ? 1 : 0) +
    (filterSize !== 'all' ? 1 : 0) +
    (filterBreed !== 'all' ? 1 : 0) +
    (filterColor !== 'all' ? 1 : 0) +
    selectedTraits.length +
    (onlyVaccinated ? 1 : 0) +
    (onlyNeutered ? 1 : 0) +
    (onlyDewormed ? 1 : 0);

  // Client-side filtering (applied on top of whichever dataset is active)
  const filtered = sourceData.filter(a => {
    if (filterSpecies !== 'all' && a.species !== filterSpecies) return false;
    if (filterGender !== 'all' && a.gender !== filterGender) return false;
    if (filterBreed !== 'all' && a.breed !== filterBreed) return false;
    if (filterColor !== 'all' && a.color !== filterColor) return false;

    if (filterAge !== 'all') {
      const age = a.age;
      if (age == null) return false;
      if (filterAge === 'baby' && age >= 1) return false;
      if (filterAge === 'young' && (age < 1 || age >= 3)) return false;
      if (filterAge === 'adult' && (age < 3 || age >= 8)) return false;
      if (filterAge === 'senior' && age < 8) return false;
    }

    if (filterSize !== 'all') {
      const w = a.weightKg;
      if (w == null) return false;
      if (filterSize === 'small' && w >= 10) return false;
      if (filterSize === 'medium' && (w < 10 || w > 25)) return false;
      if (filterSize === 'large' && w <= 25) return false;
    }

    if (selectedTraits.length) {
      const traits = traitsOf(a);
      if (!selectedTraits.every(t => traits.includes(toCanonical(t)))) return false;
    }

    if (onlyVaccinated && a.isVaccinated !== true) return false;
    if (onlyNeutered && a.isNeutered !== true) return false;
    if (onlyDewormed && a.isDewormed !== true) return false;

    // Client-side text fallback (server search already handled text when active)
    if (!useServerSearch && searchTerm) {
      const q = searchTerm.toLowerCase();
      const match =
        (a.name || '').toLowerCase().includes(q) ||
        (a.breed || '').toLowerCase().includes(q) ||
        (a.bio || '').toLowerCase().includes(q) ||
        (a.traits || []).some(t => t.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  const isLoading = loading || searching;

  const selectClass =
    'border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed';

  const statusBadgeClass = (status: string) => {
    const map: Record<string, string> = {
      Available: 'badge-available',
      Adopted: 'badge-adopted',
      Foster: 'badge-foster',
      Medical: 'badge-medical',
      StrayHold: 'badge-strayhold',
      Quarantine: 'badge-quarantine',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Gazdikeresők</h1>

        <div className="flex gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Keresés (pl. barátságos labrador)..."
              className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none w-full sm:w-64 bg-white"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`relative p-2.5 rounded-lg border transition-colors ${
              showFilters ? 'bg-teal-50 border-teal-300 text-teal-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            aria-label="Szűrők"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-teal-600 text-white text-[10px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="filter-panel bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Species */}
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-600">Faj</span>
              <select
                value={filterSpecies}
                onChange={e => {
                  setFilterSpecies(e.target.value as Species | 'all');
                  setFilterBreed('all');
                }}
                className={selectClass}
              >
                <option value="all">Mindegy</option>
                <option value="Kutya">Kutya</option>
                <option value="Macska">Macska</option>
                <option value="Egyéb">Egyéb</option>
              </select>
            </label>

            {/* Gender */}
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-600">Nem</span>
              <select value={filterGender} onChange={e => setFilterGender(e.target.value as Gender | 'all')} className={selectClass}>
                <option value="all">Mindegy</option>
                <option value="Kan">Kan</option>
                <option value="Nőstény">Nőstény</option>
              </select>
            </label>

            {/* Age */}
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-600">Életkor</span>
              <select value={filterAge} onChange={e => setFilterAge(e.target.value as AgeBucket)} className={selectClass}>
                <option value="all">Mindegy</option>
                <option value="baby">Kölyök (&lt; 1 év)</option>
                <option value="young">Fiatal (1–3 év)</option>
                <option value="adult">Felnőtt (3–8 év)</option>
                <option value="senior">Idős (8+ év)</option>
              </select>
            </label>

            {/* Size (weight) */}
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-600">Méret</span>
              <select value={filterSize} onChange={e => setFilterSize(e.target.value as SizeBucket)} className={selectClass}>
                <option value="all">Mindegy</option>
                <option value="small">Kicsi (&lt; 10 kg)</option>
                <option value="medium">Közepes (10–25 kg)</option>
                <option value="large">Nagy (&gt; 25 kg)</option>
              </select>
            </label>

            {/* Breed */}
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-600">Fajta</span>
              <select
                value={filterBreed}
                onChange={e => setFilterBreed(e.target.value)}
                className={selectClass}
                disabled={breedOptions.length === 0}
              >
                <option value="all">Mindegy</option>
                {breedOptions.map(b => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>

            {/* Color */}
            {colorOptions.length > 0 && (
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-600">Szín</span>
                <select value={filterColor} onChange={e => setFilterColor(e.target.value)} className={selectClass}>
                  <option value="all">Mindegy</option>
                  {colorOptions.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {/* Traits (multi-select chips) */}
          {traitOptions.length > 0 && (
            <div className="space-y-2">
              <span className="text-sm font-medium text-gray-600">Tulajdonságok</span>
              <div className="flex flex-wrap gap-2">
                {traitOptions.map(t => {
                  const active = isTraitSelected(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTrait(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-teal-600 border-teal-600 text-white'
                          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Health flags + clear */}
          <div className="flex flex-wrap items-center gap-4 pt-1 border-t border-gray-100">
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
              <input
                type="checkbox"
                checked={onlyVaccinated}
                onChange={e => setOnlyVaccinated(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Oltott
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
              <input
                type="checkbox"
                checked={onlyNeutered}
                onChange={e => setOnlyNeutered(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Ivartalanított
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
              <input
                type="checkbox"
                checked={onlyDewormed}
                onChange={e => setOnlyDewormed(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Féregtelenített
            </label>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto mt-3 text-sm text-teal-600 font-medium hover:underline"
              >
                Szűrők törlése ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Result count */}
      {!isLoading && (searchTerm || activeFilterCount > 0) && (
        <p className="text-sm text-gray-500">{filtered.length} találat</p>
      )}

      {/* Animal grid */}
      {isLoading ? (
        <AnimalGridSkeleton count={8} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-700 mb-1">Nincs találat</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            {searchTerm || activeFilterCount > 0
              ? 'Nincs a keresésnek és szűrőknek megfelelő állat. Próbálj tágítani a feltételeken!'
              : 'Jelenleg nincs elérhető állat ebben a kategóriában.'}
          </p>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="mt-4 text-teal-600 font-semibold hover:underline text-sm">
              Szűrők törlése
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(animal => (
            <Link
              key={animal.id}
              to={`/animals/${animal.id}`}
              className="animal-card bg-white rounded-xl border border-gray-100 overflow-hidden"
            >
              <div className="aspect-[4/3] bg-gray-200 overflow-hidden">
                {getImageUrl(animal) ? (
                  <img src={getImageUrl(animal)!} alt={animal.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">Nincs kép</div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900">{animal.name}</h3>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusBadgeClass(animal.status)}`}>
                    {animal.status === 'Available' ? 'Elérhető' : animal.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {animal.breed} · {animal.age ? `${animal.age} éves` : 'Ismeretlen kor'} · {animal.gender || 'Ismeretlen nem'}
                </p>
                {animal.bio && (
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{animal.bio}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnimalListPage;
